import PropTypes from 'prop-types'
import clsx from 'clsx'

import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'

import { makeFormPropTypes } from '../../../../common/formikPropTypes'
import { shippingSend1FromPropTypes } from '../../../../common/propTypes'
import useTranslations from '../../../../i18n/useTranslations'
import { ADDRESS_STATES, STEPS } from '../../../../common/constants'
import AddressBook from '../../../../context/ShippingSend/AddressBook'

import { ReactComponent as Delete } from '../../../../assets/icons/trash.svg'
import { ReactComponent as Edit } from '../../../../assets/icons/edit.svg'
import { ReactComponent as Agenda } from '../../../../assets/icons/agenda.svg'
import { ReactComponent as Notepad } from '../../../../assets/icons/notepad.svg'
import { ReactComponent as MapPin } from '../../../../assets/icons/map-pin.svg'
import { ReactComponent as Sustainable } from '../../../../assets/icons/sustainable.svg'
import countryTelephoneData from 'country-telephone-data'
import useStyles from '../styles'
import AddressList from './AddressList'
import PickupPoint from './PickupPoint'
import NewAddress from '../../../../context/ShippingSend/Step1Origin/NewAddress'
import { useUserContext } from '../../../../auth/UserProvider'
import { useState } from 'react'
import { useEffect } from 'react'
import Radio from '../../../components/radio'
import ModalLayout from '../../../modals/layout'
import { PlusCircle } from 'phosphor-react'

const AddressSection = props => {
  const {
    isCenterWithAddressesBook,
    values,
    setValues,
    onBlur,
    onChange,
    isDestination,
    addressList,
    selectAddress,
    centreCode,
    originCentreId,
    originSource,
    fieldErrors,
    autofillAddress,
    countries,
    validate,
    setErrors,
    zeroResultsGeocode,
    handleChangeBookAddress,
    changeDeliveryPointaddress,
    deliveryPoints,
    selectDeliveryPointAddress,
    selectedDeliveryPoint,
    searchedAddress,
    confirmAddress,
    confirmedAddress,
    handlePickUpErrors,
    isPudo,
  } = props

  const t = useTranslations()
  const classes = useStyles()
  const { centres } = useUserContext()
  const [deleteCreatedAddress, setDeleteCreatedAddress] = useState()
  const title = isDestination
    ? t('shipping-send-form:title:destination-address')
    : t('shipping-send-form:title:origin-address')
  const handleClickNewDirection = () => {
    setErrors({})
    if (values?.newAddress)
      setValues(values => ({
        ...values,
        address: values?.newAddress?.shippingAddress,
      }))
    else if (values?.addressBook)
      setValues(values => ({
        ...values,
        address: values?.addressBook?.shippingAddress,
      }))
    onChange({ target: { name: 'addressState', value: ADDRESS_STATES.NEW } })
  }

  const handleClickExistingDirection = () => {
    if (values.selectedAddress?.shippingAddress)
      setValues(values => ({
        ...values,
        address: {
          address: values.selectedAddress.shippingAddress.address,
          name: values.selectedAddress.shippingAddress.name,
          phone: getPhoneInfo(values.selectedAddress.shippingAddress.phone)
            ?.phone,
          phonePrefix: getPhoneInfo(
            values.selectedAddress.shippingAddress.phone,
          )?.prefix,
          email: values.selectedAddress.shippingAddress.email,
          postalCode: values.selectedAddress.shippingAddress.postalCode,
          stair: '',
          floor: '',
          letter: '',
          hasSeveralLocalities: false,
          localitiesOptions: null,
        },
      }))
    onChange({
      target: { name: 'addressState', value: ADDRESS_STATES.EXISTING },
    })
  }

  const handleClickAtPickupPoint = () =>
    onChange({
      target: { name: 'addressState', value: ADDRESS_STATES.AT_PICKUP_POINT },
    })

    const getPhoneInfo = phoneSource => {
      let phone = phoneSource?.replace(/\s/g, '')
      let prefix = '+34'
      if (phone?.length && phone[0] === '+') {
        let findPrefix = countryTelephoneData.allCountries
        .reduce((longestMatch, { dialCode }) => {
          if (phone.startsWith('+' + dialCode) && dialCode.length > longestMatch.length) {
            return dialCode;
          }
          return longestMatch;
        }, '');
  
        phone = phone.replace(
          '+' + findPrefix,
          '',
        )
        prefix = '+' + findPrefix
      }
      return { prefix: prefix, phone: phone }
    }

  useEffect(() => {
    if (
      values?.addressState === ADDRESS_STATES.NEW &&
      !values?.isAddressBookOpen &&
      values?.addressBook
    )
      setValues(values => ({
        ...values,
        address: values?.addressBook?.shippingAddress,
      }))
    else if (
      values?.addressState === ADDRESS_STATES.NEW &&
      values?.isAddressBookOpen &&
      !values?.newAddress
    )
      setValues(values => ({
        ...values,
        address: {
          country: { code: 'ES', label: 'España' },
          address: '',
          name: '',
          phone: '',
          phonePrefix: '+34',
          email: '',
          stair: '',
          floor: '',
          letter: '',
          postalCode: '',
          province: '',
          city: '',
          hasSeveralLocalities: false,
          localitiesOptions: null,
        },
      }))
    else if (
      values?.addressState === ADDRESS_STATES.AT_PICKUP_POINT &&
      selectedDeliveryPoint
    )
      selectDeliveryPointAddress(selectedDeliveryPoint)
  }, [values.addressState, values.isAddressBookOpen])

  const renderDeleteCreatedAddress = () => {
    return (
      <ModalLayout
        isOpen={deleteCreatedAddress}
        title={t('action:delete-created-address-header')}
        content={t('action:delete-created-address-body')}
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                setValues(values => ({
                  ...values,
                  address: values?.newAddress?.shippingAddress,
                  addressBook: null,
                }))
                setDeleteCreatedAddress(null)
              }}
            >
              {t('action:cancel')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setValues(values => ({
                  ...values,
                  newAddress: null,
                  address: values?.addressBook?.shippingAddress ?? {
                    email: '',
                    name: '',
                    phone: '',
                    postalCode: '',
                    phonePrefix: '+34',
                    floor: '',
                    letter: '',
                    stair: '',
                    country: { code: 'ES', label: '' },
                    address: '',
                    province: '',
                    city: '',
                    hasSeveralLocalities: false,
                    localitiesOptions: null,
                  },
                }))
                setDeleteCreatedAddress(null)
              }}
            >
              {t('action:confirm')}
            </Button>
          </>
        }
      />
    )
  }

  const formatAddress = details => {
    let { address, stair, floor, letter } = details

    if (stair) {
      address += `, escalera ${stair}`
    }

    if (floor) {
      address += `, piso ${floor}`
    }

    if (letter) {
      address += `, letra ${letter}`
    }

    return address
  }

  return (
    <Box component="section" className={classes.section}>
      {renderDeleteCreatedAddress()}
      <Typography
        variant="h2"
        className={clsx('secondaryTitle', 'decorator', classes.title)}
      >
        {title}
      </Typography>
      <Box mb={3} sx={{ display: 'flex' }}>
        <Button
          size="small"
          startIcon={<Notepad weight="regular" />}
          variant={
            values.addressState === ADDRESS_STATES.EXISTING
              ? 'contained'
              : 'outlined'
          }
          onClick={handleClickExistingDirection}
        >
          {t('shipping-send-form:label:contract-address')}
        </Button>
        <Button
          size="small"
          startIcon={<Agenda weight="regular" />}
          variant={
            values.addressState === ADDRESS_STATES.NEW
              ? 'contained'
              : 'outlined'
          }
          onClick={handleClickNewDirection}
        >
          {t('shipping-send-form:label:other-address')}
        </Button>
        {!isPudo && (
          <Tooltip
            title={
              <Typography variant="body2">
                {t('message:most-sustainable-option')}
              </Typography>
            }
            placement="top"
            sx={{ order: '2' }}
          >
            <Button
              size="small"
              startIcon={<Sustainable weight="regular" />}
              className={
                values.addressState === ADDRESS_STATES.AT_PICKUP_POINT
                  ? classes.cttExpressPointButtonContained
                  : classes.cttExpressPointButtonOutlined
              }
              onClick={handleClickAtPickupPoint}
            >
              {t('shipping-send-form:label:pick-up-point')}
            </Button>
          </Tooltip>
        )}
      </Box>

      {values.addressState === ADDRESS_STATES.EXISTING ? (
        <>
          <AddressList
            addressList={addressList}
            originCentreId={originCentreId}
            originSource={originSource}
            selected={values.address}
            selectAddress={selectAddress}
          />
        </>
      ) : values.addressState === ADDRESS_STATES.NEW ? (
        <>
          {values.newAddress ? (
            <Box className={classes.radiosContainer}>
              <Box
                key={values.newAddress.id}
                className={clsx(classes.radioWrapper, {
                  'is-checked': true,
                })}
              >
                <Radio
                  className={classes.radio}
                  label={values.newAddress.name}
                  subLabel={t('address:without-name', {
                    address: values.newAddress.shippingAddress.address,
                    postalCode: values.newAddress.shippingAddress.postalCode,
                    city: values.newAddress.shippingAddress.city,
                    country:
                      values.newAddress.shippingAddress.country?.code ??
                      values.newAddress.shippingAddress.country,
                  })}
                  additionalText={
                    values.newAddress.shippingAddress.email ||
                    values.newAddress.shippingAddress.phone
                      ? `${values.newAddress.shippingAddress.email || ''}${
                          values.newAddress.shippingAddress.email &&
                          values.newAddress.shippingAddress.phone
                            ? ', '
                            : ''
                        }${values.newAddress.shippingAddress.phonePrefix || ''}${values.newAddress.shippingAddress.phone || ''}`
                      : null
                  }
                  multiline={false}
                  checked={true}
                />
                <Box className={classes.buttons}>
                  <IconButton
                    onClick={() => onChange({ target: { name: 'isAddressBookOpen', value: values?.newAddress } })}
                    className={classes.iconButton}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => setDeleteCreatedAddress(true)}
                    className={classes.iconButton}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : null}
          <Box className={classes.newAddressButton}>
            <Link
              className={classes.plusIcon}
              onClick={() => {
                setErrors({})
                setValues(values => ({
                  ...values,
                  address: {
                    email: '',
                    name: '',
                    phone: '',
                    postalCode: '',
                    phonePrefix: '+34',
                    floor: '',
                    letter: '',
                    stair: '',
                    country: { code: 'ES', label: 'España' },
                    address: '',
                    province: '',
                    city: '',
                    hasSeveralLocalities: false,
                    localitiesOptions: null,
                  },
                }))
                onChange({ target: { name: 'isAddressBookOpen', value: true } })
              }}
            >
              <PlusCircle />
              {t('action:new-address')}
            </Link>
          </Box>
          <AddressBook
            currentStep={STEPS.TO}
            centerCode={centreCode}
            currentAddressBook={values?.addressBook?.shippingAddress ?? true}
            selectDirection={e => {
              handleChangeBookAddress(e)
              if (values?.newAddress) setDeleteCreatedAddress(true)
            }}
            hasNewAddress={values?.isAddressBookOpen}
          />
          <NewAddress
            countries={countries}
            isOpen={values?.isAddressBookOpen}
            close={() => onChange({ target: { name: 'isAddressBookOpen', value: false } })}
            onSubmit={direction => {
              // REVIEW: considerar vaciar `selectedAddress` y guardar
              // únicamente en `addressBook`. En otras partes del código
              // se podría saber de dónde sacar la información en base a
              // la propiedad `addressSource`.
              direction.address = direction?.address
                ? formatAddress(direction)
                : ''
              setValues(values => ({
                ...values,
                //selectedAddress: null,
                address: {
                  email: direction?.email ?? '',
                  name: direction?.name ?? '',
                  phone: getPhoneInfo(direction?.phone)?.phone ?? '',
                  postalCode: direction?.postalCode ?? '',
                  phonePrefix:
                    direction?.phonePrefix ??
                    getPhoneInfo(direction?.phone)?.prefix,
                  floor: '',
                  letter: '',
                  stair: '',
                  country: {
                    code: direction?.country?.code,
                    label: countries.find(
                      ({ code }) => code === direction?.country?.code,
                    )?.label,
                  },
                  address: direction?.address ?? '',
                  province: direction?.province ?? '',
                  city: direction?.city ?? '',
                  hasSeveralLocalities: false,
                  localitiesOptions: null,
                },
                addressBook: null,
                newAddress: {
                  id: direction.id,
                  name: direction.name,
                  shippingAddress: {
                    ...direction,
                  },
                },
              }))
            }}
            centerCode={centreCode}
            clientCode={originCentreId}
            onChange={onChange}
            values={values}
            onBlur={onBlur}
            errors={fieldErrors}
            zeroResultsGeocode={zeroResultsGeocode}
            autofillAddress={autofillAddress}
            setErrors={setErrors}
            validate={validate}
            isPhoneRequired
          />
        </>
      ) : values.addressState === ADDRESS_STATES.AT_PICKUP_POINT ? (
        <PickupPoint
          countryRestrictions={['ES', 'PT']}
          changeAddress={changeDeliveryPointaddress}
          deliveryPoints={deliveryPoints}
          selectAddress={selectDeliveryPointAddress}
          selectedAddress={selectedDeliveryPoint}
          searchedAddress={searchedAddress}
          onChange={onChange}
          onBlur={onBlur}
          values={values}
          confirmAddress={confirmAddress}
          confirmedAddress={confirmedAddress}
          fieldErrors={fieldErrors}
        />
      ) : null}

      {selectedDeliveryPoint &&
      ((values.addressState === 'EXISTING' &&
        !values.address.phone.length &&
        !centres.find(({ id }) => values.address.id === id)) ||
        (!isCenterWithAddressesBook && values.addressState === 'EXISTING')) ? (
        <Alert severity="error" className={classes.alertStepTwo}>
          {!isCenterWithAddressesBook && values.addressState === 'EXISTING'
            ? t('shipping-send-form:address:no-address')
            : t('shipping-send-form:address:no-phone-error-center')}
        </Alert>
      ) : null}
    </Box>
  )
}

AddressSection.propTypes = {
  isDestination: PropTypes.bool,

  ...makeFormPropTypes(shippingSend1FromPropTypes),
  isCenterWithAddressesBook: PropTypes.bool,
  onClickOpenAgenda: PropTypes.func,
  addressList: PropTypes.array,
  handlePickUpErrors: PropTypes.func,
}

export default AddressSection
